"use client";
import React, { type ReactNode, useState } from "react";
import type { MenuItemProps } from "react-aria-components";
import { Menu, MenuItem, MenuTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { Icon, type IconName } from "../Icon";
import { Popover } from "../Popover";
import { Tray } from "../Tray";

export interface ActionItem {
  /**
   * Unique identifier for the action
   */
  id: number | string;
  /**
   * Display name for the action
   */
  name: string;
  /**
   * Optional subtitle for the action
   */
  subtitle?: string;
  /**
   * Visual variant of the action
   */
  variant?: "primary" | "default" | "destructive";
  /**
   * Icon to display with the action
   */
  iconName?: IconName;
  /**
   * Whether the action is disabled
   */
  disabled?: boolean;
  /**
   * Handler for when the action is selected
   */
  onAction?: () => void;
  /**
   * Optional URL for navigation. When set, the menu item renders as a link.
   */
  href?: string;
  /**
   * Open `href` in a new tab. Ignored without `href`.
   */
  openInNewTab?: boolean;
  /**
   * Test ID for testing
   */
  testId?: string;
  /**
   * Custom render function for the menu item content
   * If provided, this will override the default name/subtitle rendering
   */
  render?: () => ReactNode;
  /**
   * Whether to remove padding from the menu item (useful for dividers)
   */
  noPadding?: boolean;
}

export interface ActionMenuProps {
  /**
   * List of actions to display
   */
  items: ActionItem[];
  /**
   * Trigger button component
   */
  children: ReactNode;
  /**
   * Alignment of the popover
   * @default "left"
   */
  align?: "left" | "right";
  /**
   * Size of menu items
   * @default "md"
   */
  size?: "sm" | "md";
  /**
   * Text alignment within menu items
   * @default "left"
   */
  textAlign?: "left" | "right";
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Global action handler (called if item doesn't have its own onAction)
   */
  onAction?: (item: ActionItem) => void;
  /**
   * Optional header content rendered above the menu items
   */
  header?: ReactNode;
  /**
   * Optional footer content rendered below the menu items
   */
  footer?: ReactNode;
  /**
   * Whether to use a Tray (bottom sheet) on mobile devices instead of a Popover
   * @default true
   */
  useMobileTray?: boolean;
  /**
   * Additional CSS classes for the Popover (desktop only)
   */
  popoverClassName?: string;
}

/**
 * ActionMenu
 *
 * A dropdown menu for actions, typically triggered by a button.
 * Supports icons, destructive actions, and flexible alignment.
 */
export function ActionMenu({
  children,
  items,
  className,
  align = "left",
  textAlign = "left",
  size = "md",
  onAction,
  header,
  footer,
  useMobileTray = true,
  popoverClassName,
}: ActionMenuProps) {
  const [isTrayOpen, setIsTrayOpen] = useState(false);
  const { isMobile } = useBreakpoint();
  const shouldUseTray = useMobileTray && isMobile;

  if (shouldUseTray) {
    // Mobile: Use Tray instead of MenuTrigger/Popover
    // Clone the trigger element and add onClick handler
    const trigger = React.isValidElement(children)
      ? React.cloneElement(children as React.ReactElement<{ onPress?: () => void }>, {
          onPress: () => setIsTrayOpen(true),
        })
      : children;

    // Extract text content from trigger for tray title
    const getTriggerText = (element: ReactNode): string => {
      if (typeof element === "string") return element;
      if (React.isValidElement(element)) {
        const props = element.props as { children?: ReactNode };
        if (typeof props.children === "string") return props.children;
        if (Array.isArray(props.children)) {
          const textChild = props.children.find((child) => typeof child === "string");
          return textChild || "";
        }
      }
      return "";
    };

    const triggerText = getTriggerText(children);
    const trayTitle = triggerText || "Actions";

    return (
      <>
        <div className={className}>{trigger}</div>
        <Tray
          isOpen={isTrayOpen}
          onClose={() => setIsTrayOpen(false)}
          title={trayTitle}
          contentPadding={false}
          headerContent={header}
          footerContent={footer}
        >
          <div className="flex flex-col">
            {items.map((item) => {
              const Tag = item.href && !item.disabled ? "a" : "button";
              return (
                <Tag
                  key={item.id}
                  {...(item.href && !item.disabled ? { href: item.href } : {})}
                  {...(item.href && !item.disabled && item.openInNewTab ? { target: "_blank", rel: "noreferrer" } : {})}
                  onClick={() => {
                    if (!item.disabled) {
                      if (item.onAction) {
                        item.onAction();
                      } else {
                        onAction?.(item);
                      }
                      setIsTrayOpen(false);
                    }
                  }}
                  disabled={item.disabled}
                  data-testid={item.testId}
                  className={twMerge(
                    "flex w-full items-center text-start transition-colors",
                    !item.noPadding && "px-6 py-3",
                    "border-b border-border-muted last:border-0",
                    item.variant === "destructive"
                      ? "text-feedback-error-text hover:bg-feedback-error-background active:bg-feedback-error-background/80"
                      : "text-text-primary hover:bg-background-hover active:bg-background-hover/80",
                    item.disabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {item.iconName && <Icon name={item.iconName} size={20} className="mr-3 flex-shrink-0" />}
                  <div className="flex flex-1 flex-col">
                    {item.render ? (
                      item.render()
                    ) : (
                      <>
                        <span className={item.subtitle ? "font-medium" : ""}>{item.name}</span>
                        {item.subtitle && <span className="text-sm text-text-secondary">{item.subtitle}</span>}
                      </>
                    )}
                  </div>
                </Tag>
              );
            })}
          </div>
        </Tray>
      </>
    );
  }

  // Desktop: Use MenuTrigger/Popover
  return (
    <div className={className}>
      <MenuTrigger>
        {children}
        <Popover
          className={twMerge(
            "w-fit min-w-[180px] overflow-auto rounded-[var(--control-border-radius)] border border-border-default",
            "bg-background-surface shadow-lg",
            // Enter animations
            "animate-in fade-in zoom-in-97",
            "duration-200 ease-out",
            // Exit animations
            "data-[exiting]:animate-out data-[exiting]:fade-out data-[exiting]:zoom-out-97",
            "data-[exiting]:duration-150 data-[exiting]:ease-in",
            // Origin for zoom
            align === "right" ? "origin-top-right" : "origin-top-left",
            popoverClassName
          )}
          placement={align === "right" ? "bottom end" : "bottom start"}
        >
          {/* py-1 keeps the first and last rows off the popover border. Items
              carry only horizontal padding of their own, so without it the top
              and bottom rows sit flush against the edge.

              popoverClassName is merged last so consumers can still override
              it; that is intentional. No current consumer passes vertical
              padding (both pass min-w-* only), but one that does will replace
              this default rather than compose with it. */}
          <div className={twMerge("flex min-w-[180px] flex-col gap-1 py-1", popoverClassName)}>
            {header ? <div className="px-3 pb-2 pt-3">{header}</div> : null}
            <Menu className="!outline-none">
              {items.map((item) => (
                <ActionMenuItem
                  key={item.id}
                  id={item.id}
                  href={item.href}
                  {...(item.href && item.openInNewTab ? { target: "_blank", rel: "noreferrer" } : {})}
                  onAction={() => {
                    if (item.onAction) {
                      item.onAction();
                    } else {
                      onAction?.(item);
                    }
                  }}
                  textAlign={textAlign}
                  size={size}
                  iconName={item.iconName}
                  isDisabled={item.disabled}
                  variant={item.variant}
                  subtitle={item.subtitle}
                  render={item.render}
                  noPadding={item.noPadding}
                  data-testid={item.testId}
                >
                  {item.name}
                </ActionMenuItem>
              ))}
            </Menu>
            {footer ? <div className="px-3 pb-3 pt-2">{footer}</div> : null}
          </div>
        </Popover>
      </MenuTrigger>
    </div>
  );
}

interface ActionMenuItemProps extends Omit<MenuItemProps, "children" | "render"> {
  children: ReactNode;
  textAlign?: "left" | "right";
  size?: "sm" | "md";
  iconName?: IconName;
  variant?: "primary" | "default" | "destructive";
  subtitle?: string;
  render?: () => ReactNode;
  noPadding?: boolean;
}

function ActionMenuItem({
  textAlign = "left",
  iconName,
  size = "md",
  isDisabled,
  children,
  variant = "default",
  subtitle,
  render,
  noPadding,
  ...props
}: ActionMenuItemProps) {
  const sizeClasses = {
    sm: "px-3 py-2 text-sm",
    md: subtitle ? "px-4 py-2.5 text-base" : "px-4 py-2 text-base",
  };

  const nonDestructiveVariantClasses =
    "text-text-primary hover:bg-background-hover data-[focused]:bg-background-hover data-[focus-visible]:ring-2 data-[focus-visible]:ring-action-primary data-[focus-visible]:ring-inset";

  const variantClasses = {
    primary: nonDestructiveVariantClasses,
    default: nonDestructiveVariantClasses,
    destructive:
      "text-feedback-error-text hover:bg-feedback-error-background data-[focused]:bg-feedback-error-background data-[focus-visible]:ring-2 data-[focus-visible]:ring-feedback-error-border data-[focus-visible]:ring-inset",
  };

  // If custom render function is provided, use it
  if (render) {
    return (
      <MenuItem
        {...props}
        isDisabled={isDisabled}
        className={twMerge(
          "group box-border flex w-full cursor-pointer items-center rounded-sm outline-none transition-colors",
          !noPadding && sizeClasses[size],
          variantClasses[variant],
          textAlign === "right" ? "justify-end" : "justify-start",
          isDisabled && "pointer-events-none opacity-50"
        )}
      >
        {render()}
      </MenuItem>
    );
  }

  // Default rendering with optional subtitle
  return (
    <MenuItem
      {...props}
      isDisabled={isDisabled}
      className={twMerge(
        "group box-border flex w-full cursor-pointer items-center rounded-sm outline-none transition-colors",
        !noPadding && sizeClasses[size],
        variantClasses[variant],
        textAlign === "right" ? "justify-end" : "justify-start",
        isDisabled && "pointer-events-none opacity-50"
      )}
    >
      {iconName && textAlign === "left" && (
        <Icon name={iconName} size={size === "sm" ? 16 : 20} className="mr-3 flex-shrink-0" />
      )}
      <div className="flex flex-1 flex-col">
        <span className={subtitle ? "font-medium" : ""}>{children}</span>
        {subtitle && <span className="text-sm text-text-secondary">{subtitle}</span>}
      </div>
      {iconName && textAlign === "right" && (
        <Icon name={iconName} size={size === "sm" ? 16 : 20} className="ml-3 flex-shrink-0" />
      )}
    </MenuItem>
  );
}
